import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import Avatar from '../components/Avatar';
import useCoachAvatars from '../hooks/useCoachAvatars';

const Bills = () => {
  const [bills, setBills] = useState([]);
  const [rollingUp, setRollingUp] = useState(false);
  const [message, setMessage] = useState(null);
  // WHICH MONTH TO BILL.
  //
  // The button used to roll up whatever month it happened to be, with no way
  // to choose. That is wrong about half the time: billing October happens
  // either on the last day of October or in the first days of November, so
  // neither "this month" nor "last month" is a default that holds.
  //
  // So it does not guess. The server is asked which months actually have
  // approved hours waiting, and the oldest of those is offered, because that
  // is the one somebody is overdue on. Current month only as a last resort,
  // when there is nothing outstanding at all and the picker is really just
  // somewhere to start.
  const thisMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const [period, setPeriod] = useState(thisMonth);
  const [pending, setPending] = useState([]);
  // One request for the page, looked up per row. See useCoachAvatars.
  const avatars = useCoachAvatars();

  const load = () => api.get('/api/bills').then(res => setBills(res.data));

  // Refreshed after every roll-up as well as on load, so the "waiting to be
  // billed" line empties out as the work is done rather than going stale.
  const loadPending = () => api.get('/api/bills/pending-periods')
    .then(res => {
      setPending(res.data);
      // Only steer the picker on first load. Moving it under the admin after
      // they have chosen a month would be maddening.
      if (res.data.length > 0) {
        setPeriod(prev => (prev === thisMonth() ? res.data[0].period_label : prev));
      }
    })
    .catch(() => { /* The picker still works. No need to shout about it. */ });

  useEffect(() => { load(); loadPending(); }, []);

  const rollUp = async () => {
    setRollingUp(true);
    setMessage(null);
    try {
      const res = await api.post('/api/bills/roll-up', { period_label: period });
      const n = res.data.bills.length;
      // Nothing to bill is not a failure, so it is not dressed as one. The old
      // version put a red error box up whenever FreeAgent was unconfigured,
      // which is what the club read as the portal being broken.
      setMessage({
        type: n > 0 ? 'success' : 'notice',
        text: n > 0
          ? `${n} invoice${n === 1 ? '' : 's'} raised for ${period}.`
          : `There were no approved hours left to bill for ${period}. `
            + 'Anything still waiting is on the Submissions screen, needing approval first.'
      });
      load();
      loadPending();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Roll-up failed' });
    } finally {
      setRollingUp(false);
    }
  };

  const markPaid = async (id) => {
    if (!window.confirm('Confirm this has been paid via NatWest?')) return;
    setMessage(null);
    try {
      await api.post(`/api/bills/${id}/mark-paid`);
      load();
      loadPending();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'That could not be marked paid.' });
    }
  };

  // The way back from a mis-click, and the repair for an invoice that was
  // marked paid before it should have been.
  const unmarkPaid = async (bill) => {
    if (!window.confirm(
      `Put invoice ${bill.invoice_number || ''} back to unpaid?

`
      + 'Use this if it was marked paid by mistake, or if money still has to go out on it.'
    )) return;
    setMessage(null);
    try {
      await api.post(`/api/bills/${bill.id}/unmark-paid`);
      load();
      loadPending();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'That could not be changed back.' });
    }
  };

  return (
    <>
      <h1>Bills</h1>
      <div className="card">
        <h2>Monthly roll-up</h2>
        <p className="muted">
          Turns every approved session in the chosen month into one invoice per coach, raised in
          that coach's name. This never touches payment — pay each one via NatWest as usual, then
          mark it paid below. Running it twice is safe. Anything approved late joins the coach's open invoice for
          that month, or starts a new one if theirs has already been paid, because a paid
          invoice is never altered.
        </p>
        {message && <div className={`${message.type === 'error' ? 'error-msg' : message.type === 'notice' ? 'notice-msg' : 'success-msg'} prewrap`}>{message.text}</div>}
        {pending.length > 0 && (
          <div className="pending-periods">
            <strong>Waiting to be invoiced:</strong>{' '}
            {pending.map((p, i) => (
              <React.Fragment key={p.period_label}>
                {i > 0 && ', '}
                <button
                  type="button"
                  className={`linkish${p.period_label === period ? ' current' : ''}`}
                  onClick={() => setPeriod(p.period_label)}
                >
                  {p.period_label} ({p.sessions} session{p.sessions === 1 ? '' : 's'},{' '}
                  {p.coaches} coach{p.coaches === 1 ? '' : 'es'}, £{Number(p.total).toFixed(2)})
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="rollup-controls">
          <div className="form-row">
            <label htmlFor="rollup-period">Month to bill</label>
            <input
              id="rollup-period"
              name="period"
              type="month"
              value={period}
              onChange={e => setPeriod(e.target.value)}
            />
          </div>
          <button className="btn orange" onClick={rollUp} disabled={rollingUp || !/^\d{4}-\d{2}$/.test(period)}>
            {rollingUp ? 'Rolling up…' : 'Raise invoices for this month'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>All bills</h2>
        {bills.length === 0 ? <p className="muted">No bills yet.</p> : (
          <table>
            <thead><tr><th>Invoice</th><th>Coach</th><th>Period</th><th>Hours</th><th>Mileage</th><th>Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {bills.map(b => (
                <tr key={b.id}>
                  <td>{b.invoice_number || <span className="muted">—</span>}</td>
                  <td>
                    <span className="cell-with-avatar">
                      <Avatar name={b.coach_name} src={avatars[b.coach_id]} size="sm" />
                      {b.coach_name}
                    </span>
                  </td>
                  <td>{b.period_label}</td>
                  <td>£{Number(b.total_hours_amount).toFixed(2)}</td>
                  <td>£{Number(b.total_mileage_amount).toFixed(2)}</td>
                  <td>£{Number(b.total_amount).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${b.status}`}>{b.status === 'paid' ? 'Paid' : 'Awaiting payment'}</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <Link className="btn small secondary" to={`/invoice/${b.id}`}>Invoice</Link>
                      {b.status !== 'paid'
                        ? <button className="btn small secondary" onClick={() => markPaid(b.id)}>Mark paid</button>
                        : <button className="btn small secondary" onClick={() => unmarkPaid(b)}>Not paid after all</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default Bills;
